'use client';

import { AppBadgeHost } from '@/components/layout/app-badge-host';
import { LocationPromptHost } from '@/components/layout/location-prompt-host';
import { TourAutoStarter } from '@/components/layout/tours/tour-auto-starter';
import { TourHost } from '@/components/layout/tours/tour-host';
import { AppNavOverlay } from '@/components/sidebar/app-nav-overlay';
import { TourLinkWatcher } from '@/components/layout/tours/tour-link-watcher';
import { WhatsNewHost } from '@/components/layout/whats-new/whats-new-host';
import { AppSidebar } from '@/components/sidebar';
import { AccessGateWatcher } from '@/shared/components/providers/access-gate';
import { PauseGate } from '@/shared/components/providers/pause-gate';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import {
  FULL_SURFACE_PATTERNS,
  GLOW_PATTERNS,
  GUEST_IMMERSIVE_PATTERNS,
  HIDDEN_SEARCH_PATTERNS,
  matchesAny,
} from '@/shared/lib/chrome/route-classes';
import { useAuthStore } from '@/shared/stores/auth-store';
import { usePathname, useRouter } from 'next/navigation';
import { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { AppShell, useAppShell } from './app-shell';
import { TopHeader } from './top-header';

const PIP_RETURN_URL_KEY = 'tabootv_pip_return_url';

// Proxy-maintained auth mirror (applyChromeHints) — cosmetic chrome only,
// never an access decision.
function readAuthHint(): boolean {
  return /(?:^|;\s*)auth_hint=1(?:;|$)/.test(document.cookie);
}

/**
 * Single platform shell: prerenders auth- and pathname-neutral markup so the
 * route group ships a static shell; guest/member and route-class chrome
 * resolve client-side via the pre-hydration chrome pattern (pre-paint
 * scripts + globals.css guards + ChromePathnameProvider).
 */
export function PlatformShell({ children }: { children: React.ReactNode }) {
  const pathname = useChromePathname();
  // Starts auth-neutral for a deterministic hydration; resolved pre-paint below.
  const [isGuest, setIsGuest] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authHydrated = useAuthStore((s) => s._hasHydrated);

  useLayoutEffect(() => {
    setIsGuest(!readAuthHint());
  }, []);

  // In-place login/logout (OTP gate) must swap chrome without navigation; the
  // _hasHydrated gate keeps the store's signed-out default from overriding the
  // cookie-derived first resolve.
  useEffect(() => {
    if (authHydrated) setIsGuest(!isAuthenticated);
  }, [authHydrated, isAuthenticated]);

  const showGlow = pathname !== null && matchesAny(GLOW_PATTERNS, pathname);
  const isFullSurfaceRoute = pathname !== null && matchesAny(FULL_SURFACE_PATTERNS, pathname);

  return (
    <AppShell
      header={<PlatformHeader isGuest={isGuest} pathname={pathname} />}
      sidebar={<AppSidebar />}
      {...(isGuest ? { extraImmersivePatterns: GUEST_IMMERSIVE_PATTERNS } : {})}
      showGlow={showGlow}
      showFooter={!isFullSurfaceRoute}
    >
      {/* Redirect logic only — renders null; Suspense keeps the pathname read
          out of the static shell while children stay in it. */}
      <Suspense>
        <AccessGateWatcher />
      </Suspense>
      {/* No-op for guests, so mounting unconditionally is safe. */}
      <PauseGate>{children}</PauseGate>
      <Suspense>
        <PipReturnWatcher />
      </Suspense>
      {/* Guests reach /ai too, and its ☰ opens this overlay. Inert until open. */}
      <AppNavOverlay />
      {/* LocationPromptHost has no auth clause of its own — guests would be
          prompted if mounted. */}
      {!isGuest && (
        <Suspense>
          <TourLinkWatcher />
          <TourAutoStarter />
          <TourHost />
          <WhatsNewHost />
          <AppBadgeHost />
          <LocationPromptHost />
        </Suspense>
      )}
    </AppShell>
  );
}

function PlatformHeader({ isGuest, pathname }: { isGuest: boolean; pathname: string | null }) {
  const { isImmersive } = useAppShell();
  const hiddenSearch =
    isGuest || (pathname !== null && matchesAny(HIDDEN_SEARCH_PATTERNS, pathname));
  return <TopHeader transparentUntilScroll={isImmersive} hiddenSearch={hiddenSearch} />;
}

/**
 * Keeps the real usePathname(): the storedUrl === pathname compare must never
 * evaluate against null, or a hydration pass would spuriously re-push the
 * current URL.
 */
function PipReturnWatcher() {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const storedUrl = sessionStorage.getItem(PIP_RETURN_URL_KEY);
    if (!storedUrl) return;

    if (storedUrl === pathname) {
      sessionStorage.removeItem(PIP_RETURN_URL_KEY);
      return;
    }

    if (!document.pictureInPictureElement) {
      sessionStorage.removeItem(PIP_RETURN_URL_KEY);
      router.push(storedUrl);
      return;
    }

    // Poll for PiP close — `leavepictureinpicture` doesn't bubble to document.
    const interval = setInterval(() => {
      if (!document.pictureInPictureElement) {
        clearInterval(interval);
        const url = sessionStorage.getItem(PIP_RETURN_URL_KEY);
        if (url && url !== pathname) {
          sessionStorage.removeItem(PIP_RETURN_URL_KEY);
          router.push(url);
        }
      }
    }, 200);

    return () => clearInterval(interval);
  }, [pathname, router]);

  return null;
}
